/**
 * StepPreSession - Étape 3 : Recommandations pré-séance
 * Médicaments, compléments, alimentation, hydratation
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pill, Apple, Droplets, AlertTriangle, Info, ChevronRight, X, Sparkles, Loader2 } from 'lucide-react';
import { HEALTH_WARNINGS, getHydrationMl, getPreFoodForLevel, getSupplementsForProfile } from '../../data/nutrition';
import goSportAI from '../../services';

export function StepPreSession({ level, profile, onNext, onSkip }) {
  const [expandedSupplement, setExpandedSupplement] = useState(null);
  const [aiNutrition, setAiNutrition] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const warnings = profile.medicationType
    ? HEALTH_WARNINGS[profile.medicationType] || []
    : profile.hasHypertension
      ? HEALTH_WARNINGS.hypertension
      : HEALTH_WARNINGS.none;

  const hydration = getHydrationMl(level);
  const preFood = getPreFoodForLevel(level);
  const objective = profile.objectives?.[0] || profile.evaluationAnswers?.objective || 'wellbeing';
  const supplements = getSupplementsForProfile(level, objective);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-4 py-4 space-y-5"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-white">Avant ta séance</h3>
        <button onClick={onSkip} className="text-sm text-zinc-500 hover:text-zinc-300">
          Passer &rarr;
        </button>
      </div>

      {/* Avertissements santé */}
      {warnings.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 space-y-2"
        >
          <div className="flex items-center gap-2 text-amber-400 font-medium text-sm">
            <AlertTriangle className="w-4 h-4" />
            Précautions spécifiques
          </div>
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-300/80 pl-6">• {w}</p>
          ))}
        </motion.div>
      )}

      {/* Compléments */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-purple-400 font-medium text-sm">
          <Pill className="w-4 h-4" />
          Compléments suggérés
        </div>
        <div className="space-y-2">
          {supplements.map((sup, i) => (
            <div key={i} className="relative">
              <button
                onClick={() => setExpandedSupplement(expandedSupplement === i ? null : i)}
                className="w-full flex items-center justify-between bg-zinc-800/60 border border-zinc-700/50
                  rounded-xl px-3 py-2.5 text-left hover:border-purple-500/30 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-zinc-200">{sup.name}</p>
                  <p className="text-xs text-zinc-500">{sup.dosage}</p>
                </div>
                <Info className="w-4 h-4 text-zinc-500" />
              </button>

              <AnimatePresence>
                {expandedSupplement === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 mt-1 flex items-start gap-2">
                      <p className="text-xs text-zinc-400">{sup.info}</p>
                      <button onClick={() => setExpandedSupplement(null)} className="text-zinc-600">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>

      {/* Alimentation */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-green-400 font-medium text-sm">
          <Apple className="w-4 h-4" />
          Alimentation
        </div>

        <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 space-y-3">
          <div>
            <p className="text-xs font-medium text-zinc-400 mb-1">2h avant :</p>
            <div className="flex flex-wrap gap-1.5">
              {preFood.map((food, i) => (
                <span key={i} className="text-xs bg-green-500/10 text-green-300 px-2 py-1 rounded-lg border border-green-500/20">
                  {food}
                </span>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-zinc-400 mb-1">30 min avant :</p>
            <div className="flex flex-wrap gap-1.5">
              {['Banane', 'Dattes', 'Barre énergétique', 'Eau 500ml'].map((food, i) => (
                <span key={i} className="text-xs bg-emerald-500/10 text-emerald-300 px-2 py-1 rounded-lg border border-emerald-500/20">
                  {food}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hydratation */}
      <div className="flex items-center gap-3 bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
        <Droplets className="w-5 h-5 text-blue-400 shrink-0" />
        <div>
          <p className="text-sm font-medium text-blue-300">Hydratation recommandée</p>
          <p className="text-xs text-blue-400/70">{hydration} ml d'eau avant la séance</p>
        </div>
      </div>

      {/* AI Nutrition */}
      <div className="border border-purple-500/30 rounded-xl overflow-hidden">
        {!aiNutrition && !aiLoading ? (
          <button
            onClick={async () => {
              setAiLoading(true);
              try {
                const result = await goSportAI.generateNutritionPlan(profile, level, 'pre');
                if (result) setAiNutrition(result);
              } catch { /* ignore */ }
              setAiLoading(false);
            }}
            className="w-full px-4 py-3 flex items-center justify-center gap-2
              bg-purple-500/10 text-purple-300 text-sm font-medium
              hover:bg-purple-500/20 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Plan nutrition IA personnalisé
          </button>
        ) : aiLoading ? (
          <div className="px-4 py-4 flex items-center justify-center gap-2 text-purple-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Génération du plan…
          </div>
        ) : aiNutrition ? (
          <div className="px-4 py-3 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-purple-300">Plan IA personnalisé</span>
            </div>

            {(aiNutrition.meals || []).map((meal, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-sm">{meal.emoji || '🍽️'}</span>
                <div>
                  <p className="text-xs text-zinc-400">{meal.timing}</p>
                  <p className="text-sm text-zinc-200">{meal.suggestion}</p>
                  {meal.quick && (
                    <span className="text-[10px] text-green-400">⚡ Rapide à préparer</span>
                  )}
                </div>
              </div>
            ))}

            {aiNutrition.hydration && (
              <div className="flex items-center gap-2 bg-blue-500/10 rounded-lg px-3 py-2">
                <Droplets className="w-3.5 h-3.5 text-blue-400" />
                <p className="text-xs text-blue-300">{aiNutrition.hydration}</p>
              </div>
            )}

            {aiNutrition.tdahTip && (
              <p className="text-xs text-purple-300/70 italic">💊 {aiNutrition.tdahTip}</p>
            )}
          </div>
        ) : null}
      </div>

      {/* Next */}
      <button
        onClick={onNext}
        className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500
          text-white font-bold flex items-center justify-center gap-2"
      >
        Préparer la séance <ChevronRight className="w-5 h-5" />
      </button>
    </motion.div>
  );
}
